import { callInternal } from './internal.js';
import { normalizeAssetUrl } from '../utils/asset-url.js';

export interface TimedInspectionPlan {
	plan_id?: string | number;
	id?: string | number;
	plan?: string;
	title?: string;
	name?: string;
	store_count?: string | number;
	store_list?: Array<string | number>;
	status?: string | number;
	close_status?: string | number;
	exec_time?: string | string[];
	execute_times?: string | string[];
	cover?: string;
	cover_item?: string[];
	create_time?: string;
	last_update_time?: string;
	[k: string]: unknown;
}

export interface TimedInspectionStore {
	store_id: string | number;
	store?: string;
	store_name?: string;
	has_device?: string | number;
	[k: string]: unknown;
}

export interface TimedInspectionDevice {
	device_auto_id: string | number;
	channel_id?: string | number;
	device_id?: string | number;
	alias?: string;
	store_id?: string | number;
	device_status?: string | number;
	channel_status?: string | number;
	has_yuzhiwei?: string | number;
	has_yuzhiweis?: string | number;
	[k: string]: unknown;
}

export interface TimedInspectionPicture {
	id?: string | number;
	pic_id?: string | number;
	plan_id?: string | number;
	store_id?: string | number;
	device_auto_id?: string | number;
	channel_id?: string | number;
	pic_url?: string;
	pic_info?: string;
	create_time?: string;
	screenshot_time?: string;
	type?: string | number;
	[k: string]: unknown;
}

export interface TimedInspectionActionResult {
	ok: boolean;
	msg?: string;
	planId?: string | number;
}

export interface TimedInspectionPlanPageResult {
	ok: boolean;
	items: TimedInspectionPlan[];
	msg?: string;
	total?: number;
}

export interface TimedInspectionPictureResult {
	ok: boolean;
	items: TimedInspectionPicture[];
	msg?: string;
}

export interface TimedInspectionCreateRequest {
	title: string;
	stores: TimedInspectionStore[];
	devices: TimedInspectionDevice[];
	times: string[];
}

interface PlanOrderGroup {
	store_id: string | number;
	device: Array<{ device_auto_id: string | number; channel_id: number }>;
}

function responseMsg(code: number | string, msg?: string): string {
	return `${msg || 'Request failed'} (code=${code})`;
}

function normalizeArray<T>(data: unknown): T[] {
	if (Array.isArray(data)) return data as T[];
	if (data && typeof data === 'object') {
		const obj = data as { items?: unknown; list?: unknown; rows?: unknown; records?: unknown; data?: unknown; result?: unknown };
		for (const key of ['items', 'list', 'rows', 'records', 'data', 'result'] as const) {
			const value = obj[key];
			if (Array.isArray(value)) return value as T[];
			if (value && typeof value === 'object') {
				const nested = normalizeArray<T>(value);
				if (nested.length > 0) return nested;
			}
		}
	}
	return [];
}

function numericId(value: string | number): string | number {
	if (typeof value === 'number') return value;
	const trimmed = value.trim();
	if (/^\d+$/.test(trimmed)) return Number(trimmed);
	return value;
}

function numericChannelId(value: unknown): number {
	const parsed = Number(value ?? 1);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function getTimedInspectionPlanId(plan: TimedInspectionPlan): string | number | undefined {
	return plan.plan_id ?? plan.id;
}

export function getTimedInspectionStoreName(store: TimedInspectionStore): string {
	return store.store_name || store.store || String(store.store_id);
}

export function getTimedInspectionDeviceName(device: TimedInspectionDevice): string {
	return device.alias || String(device.device_id || device.device_auto_id);
}

export function normalizeTimedInspectionImageUrl(value: unknown): string {
	return normalizeAssetUrl(value);
}

export function getTimedInspectionPictureUrl(picture: TimedInspectionPicture | undefined): string {
	if (!picture) return '';
	return normalizeTimedInspectionImageUrl(picture.pic_url || picture.pic_info);
}

export function normalizeTimedInspectionTimes(times: unknown): string[] {
	const raw = Array.isArray(times)
		? times
		: typeof times === 'string'
			? times.split(',')
			: [];
	const seen = new Set<string>();
	for (const value of raw) {
		const text = String(value ?? '').trim();
		const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(text);
		if (!match) continue;
		seen.add(`${match[1].padStart(2, '0')}:${match[2]}`);
	}
	return Array.from(seen).sort().slice(0, 10);
}

export function extractPlanTimes(plan: TimedInspectionPlan | undefined): string[] {
	if (!plan) return [];
	const patrolPlanTimes = normalizeTimedInspectionTimes(plan.exec_time);
	if (patrolPlanTimes.length > 0) return patrolPlanTimes;
	return normalizeTimedInspectionTimes(plan.execute_times);
}

export async function fetchTimedInspectionPlans(): Promise<TimedInspectionPlanPageResult> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'inspect',
			action: '/plan/all',
			method: 'GET',
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		return { ok: true, items: normalizeArray<TimedInspectionPlan>(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchTimedInspectionStores(): Promise<TimedInspectionStore[]> {
	const res = await callInternal<unknown>({
		short_name: 'base',
		action: '/store/visible_stores',
		method: 'GET',
	});
	if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
	return normalizeArray<TimedInspectionStore>(res.data);
}

export async function fetchTimedInspectionDevices(storeIds: Array<string | number>): Promise<TimedInspectionDevice[]> {
	const results = await Promise.all(storeIds.map(async (storeId) => {
		const res = await callInternal<unknown>({
			short_name: 'base',
			action: '/fastDevice/channel_list',
			method: 'POST',
			body: { store_id: storeId, with_yuzhiwei_status: 1 },
		});
		if (Number(res.code) !== 0) throw new Error(responseMsg(res.code, res.msg));
		return normalizeArray<TimedInspectionDevice>(res.data).map((device) => ({
			...device,
			store_id: device.store_id ?? storeId,
		}));
	}));
	return results.reduce<TimedInspectionDevice[]>((acc, item) => acc.concat(item), []);
}

export async function fetchTimedInspectionTimePictures(
	planId: string | number,
	cursorId: string | number = '',
	limit = 20,
): Promise<TimedInspectionPictureResult> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'inspect',
			action: '/plan/pic_list',
			method: 'GET',
			query: { plan_id: planId, id: cursorId, limit },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		return { ok: true, items: normalizeArray<TimedInspectionPicture>(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function fetchTimedInspectionIntelligentPictures(
	planId: string | number,
	cursorId: string | number = '',
	page = 1,
	limit = 20,
): Promise<TimedInspectionPictureResult> {
	try {
		const res = await callInternal<unknown>({
			short_name: 'inspect',
			action: '/plan/store_intelligent',
			method: 'GET',
			query: { plan_id: planId, id: cursorId, page_size: page, page_limit: limit },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, items: [], msg: responseMsg(res.code, res.msg) };
		return { ok: true, items: normalizeArray<TimedInspectionPicture>(res.data) };
	} catch (e) {
		return { ok: false, items: [], msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function setTimedInspectionPlanEnabled(
	planId: string | number,
	enabled: boolean,
): Promise<TimedInspectionActionResult> {
	try {
		const res = await callInternal({
			short_name: 'inspect',
			action: enabled ? '/plan/start' : '/plan/close',
			method: 'GET',
			query: { plan_id: planId },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		return { ok: true, msg: res.msg };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

function groupDevicesByStore(devices: TimedInspectionDevice[]): PlanOrderGroup[] {
	const byStore = new Map<string, PlanOrderGroup>();
	for (const device of devices) {
		const storeId = device.store_id;
		if (storeId == null || storeId === '') continue;
		const key = String(storeId);
		const group = byStore.get(key) ?? { store_id: numericId(storeId), device: [] };
		group.device.push({
			device_auto_id: device.device_auto_id,
			channel_id: numericChannelId(device.channel_id),
		});
		byStore.set(key, group);
	}
	return Array.from(byStore.values());
}

export function buildTimedInspectionAddBody(req: TimedInspectionCreateRequest): Record<string, string | number> {
	const title = req.title.trim();
	const times = normalizeTimedInspectionTimes(req.times);
	if (!title) throw new Error('Plan name is required');
	if (req.stores.length === 0) throw new Error('Select at least one store');
	if (req.devices.length === 0) throw new Error('Select at least one device');
	if (times.length === 0) throw new Error('Select at least one inspection time');
	const planOrder = groupDevicesByStore(req.devices);
	if (planOrder.length === 0) throw new Error('Selected devices are missing store information');
	return {
		plan: title,
		exec_time: times.join(','),
		plan_order: JSON.stringify(planOrder),
	};
}

export async function createTimedInspectionPlan(req: TimedInspectionCreateRequest): Promise<TimedInspectionActionResult> {
	let body: Record<string, string | number>;
	try {
		body = buildTimedInspectionAddBody(req);
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
	try {
		const res = await callInternal<{ task_id?: string | number; plan_id?: string | number }>({
			short_name: 'inspect',
			action: '/plan/add',
			method: 'POST',
			body,
			av: '2',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		return { ok: true, msg: res.msg, planId: res.data?.plan_id ?? res.data?.task_id };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}

export async function deleteTimedInspectionPlan(planId: string | number): Promise<TimedInspectionActionResult> {
	try {
		const res = await callInternal({
			short_name: 'inspect',
			action: '/plan/delete',
			method: 'GET',
			query: { plan_id: planId },
			av: '1',
		});
		if (Number(res.code) !== 0) return { ok: false, msg: responseMsg(res.code, res.msg) };
		return { ok: true, msg: res.msg };
	} catch (e) {
		return { ok: false, msg: e instanceof Error ? e.message : String(e) };
	}
}
